// Utility to set theme based on ?theme=dark or ?theme=light in the URL
// Only 'light' and 'dark' are allowed. Any other `theme` / `data-theme`
// query values will be removed from the URL (client-side, via history.replaceState).
export function applyThemeFromQuery(): void {
  if (typeof window === 'undefined') return;
  // If the user already set a theme preference (localStorage or cookie),
  // we should not override it with a URL query parameter. This makes the
  // ThemeToggle preference take precedence over ?theme= in the URL.
  try {
    const stored = localStorage.getItem('theme');
    if (stored) return;
  } catch {
    // ignore localStorage errors
  }

  try {
    if (document.cookie && /(?:^|;)\s*theme=/.test(document.cookie)) {
      return;
    }
  } catch {
    // ignore cookie read errors
  }

  const url = new URL(window.location.href);
  const params = url.searchParams;

  // Prefer data-theme over theme if present
  const raw = params.get('data-theme') ?? params.get('theme');
  if (!raw) return;

  const theme = String(raw).trim().toLowerCase();

  // Accept only 'light' or 'dark'
  if (theme === 'light' || theme === 'dark') {
    document.documentElement.setAttribute('data-theme', theme);
    return;
  }

  // If theme is present but invalid, remove theme/data-theme from URL
  let changed = false;
  if (params.has('theme')) {
    params.delete('theme');
    changed = true;
  }
  if (params.has('data-theme')) {
    params.delete('data-theme');
    changed = true;
  }

  if (changed) {
    const newSearch = params.toString();
    const newUrl = url.pathname + (newSearch ? `?${newSearch}` : '') + url.hash;
    // Replace history entry without reloading
    window.history.replaceState({}, '', newUrl);
  }
}
